import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { ActionConfig } from '../../types/header-toolbar.type';
import { Button } from 'components/ui/button';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';

interface HeaderActionsProps {
  actions: ActionConfig[];
  selectedItems: string[];
  isMobile?: boolean;
}

interface ModelConfig {
  title: string;
  description: string;
  onConfirm: () => Promise<void>;
  preventAutoClose: boolean;
}

export const HeaderActions: React.FC<HeaderActionsProps> = ({
  actions,
  selectedItems,
  isMobile = false,
}) => {
  const { t } = useTranslation();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modelConfig, setModelConfig] = useState<ModelConfig>({
    title: '',
    description: '',
    onConfirm: async () => {
      // just an empty function
    },
    preventAutoClose: false,
  });

  const visibleActions = actions.filter(
    (action) => !action.showWhen || action.showWhen(selectedItems)
  );

  if (visibleActions.length === 0) return null;

  return (
    <div className="flex gap-2">
      {visibleActions.map((action) => {
        const Icon = action.icon;
        const showCount = selectedItems.length > 0 && action.key.includes('selected');

        return (
          <Button
            key={action.key}
            size={isMobile ? 'sm' : (action.size ?? 'sm')}
            variant={action.variant ?? 'outline'}
            className="h-8 text-sm font-bold"
            onClick={() => {
              if (action.actionConfirmationTitle && action.actionConfirmationDescription) {
                setIsConfirmationModalOpen(true);
                setModelConfig({
                  title: action.actionConfirmationTitle,
                  description: action.actionConfirmationDescription,
                  onConfirm: async () => {
                    try {
                      setIsLoading(true);
                      await action.onClick();
                      // Only close if the action completed successfully
                      setIsConfirmationModalOpen(false);
                    } catch (error) {
                      console.error('Action failed:', error);
                      // Optionally handle error (show toast, etc.)
                    } finally {
                      setIsLoading(false);
                    }
                  },
                  preventAutoClose: true, // This is key - prevents auto-close
                });
              } else {
                action.onClick();
              }
            }}
            disabled={action.disabled}
          >
            <Icon className="h-3 w-3" />
            {!isMobile && <span className='ml-1'>{t(action.label)}</span>}
            {showCount && ` (${selectedItems.length})`}
          </Button>
        );
      })}
      <ConfirmationModal
        open={isConfirmationModalOpen}
        onOpenChange={setIsConfirmationModalOpen}
        title={t(modelConfig.title)}
        description={t(modelConfig.description)}
        onConfirm={modelConfig.onConfirm}
        preventAutoClose={modelConfig.preventAutoClose}
        confirmText={t('CONFIRM')}
        cancelText={t('CANCEL')}
        isLoading={isLoading}
      />
    </div>
  );
};
